import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../../auth/AuthContext'
import type { CreateTareaDTO, PagaebleResult, SearchTareaDTO, TareaPrioridad, TareaStatus, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import { daysFromToday, formatSpokenDate, formatTimeOfDay } from '../../../utils/dates'
import * as Permisos from '../../../utils/Permisos'
import * as Roles from '../../../utils/Roles'
import { editTaskPath, enumLabel, errorMessage, isClosed, personName } from '../common/task-common'
import type { TasksLocationState } from '../common/task-common'

export interface TaskDrawerProps {
    open: boolean
    onClose: () => void
    onCountChange?: (pendientes: number) => void
}

export type TaskTab = 'pendientes' | 'vencidas' | 'hoy' | 'completadas'

export type TaskGroupKey = 'vencidas' | 'hoy' | 'proximas' | 'sinFecha' | 'completadas'

export interface TaskDrawerItem {
    id: number
    nombre: string
    descripcion: string
    group: TaskGroupKey
    checked: boolean
    busy: boolean
    prioridad: TareaPrioridad | null
    prioridadLabel: string
    tipoLabel: string
    dueText: string
    seller: string
}

export interface TaskDrawerGroup {
    key: TaskGroupKey
    label: string
    items: TaskDrawerItem[]
}

const OPEN_URL = '/tareas?status=abierta&size=-1&order=vencimiento asc'
const CLOSED_URL = '/tareas?status=cerrada&size=20&order=fechaCierre desc'
const LOAD_ERROR = 'No se pudieron cargar las tareas.'
const SAVE_ERROR = 'No se pudo actualizar la tarea.'

const GROUP_LABELS: Record<TaskGroupKey, string> = {
    vencidas: 'Vencidas',
    hoy: 'Hoy',
    proximas: 'Próximas',
    sinFecha: 'Sin fecha',
    completadas: 'Completadas recientemente',
}

const TAB_GROUPS: Record<TaskTab, TaskGroupKey[]> = {
    pendientes: ['vencidas', 'hoy', 'proximas', 'sinFecha'],
    vencidas: ['vencidas'],
    hoy: ['hoy'],
    completadas: ['completadas'],
}

const EMPTY_MESSAGES: Record<TaskTab, string> = {
    pendientes: 'No tienes tareas pendientes.',
    vencidas: 'No hay tareas vencidas.',
    hoy: 'No hay tareas para hoy.',
    completadas: 'Aún no hay tareas completadas.',
}

const TAB_LABELS: Record<TaskTab, string> = {
    pendientes: 'Pendientes',
    vencidas: 'Vencidas',
    hoy: 'Hoy',
    completadas: 'Completadas',
}

const openGroup = (vencimiento: string | null): TaskGroupKey => {
    const days = daysFromToday(vencimiento)
    if (days == null) return 'sinFecha'
    if (days < 0) return 'vencidas'
    if (days === 0) return 'hoy'
    return 'proximas'
}

const dueText = (item: SearchTareaDTO, group: TaskGroupKey) => {
    switch (group) {
        case 'vencidas': return `Venció el ${formatSpokenDate(item.vencimiento)}`
        case 'hoy': return `Hoy · ${formatTimeOfDay(item.vencimiento)}`
        case 'proximas': return `Vence el ${formatSpokenDate(item.vencimiento)}`
        case 'completadas': return item.fechaCierre ? `Completada el ${formatSpokenDate(item.fechaCierre)}` : 'Completada'
        default: return 'Sin fecha'
    }
}

const toItem = (item: SearchTareaDTO, toggling: number[]): TaskDrawerItem => {
    const closed = isClosed(item.status)
    const group = closed ? 'completadas' : openGroup(item.vencimiento)
    const busy = toggling.includes(item.id)
    return {
        id: item.id,
        nombre: item.nombre || '—',
        descripcion: item.descripcion || '',
        group,
        checked: busy ? !closed : closed,
        busy,
        prioridad: item.prioridad?.name || null,
        prioridadLabel: enumLabel(item.prioridad),
        tipoLabel: enumLabel(item.tipo),
        dueText: dueText(item, group),
        seller: item.vendedorId ? personName({ nombres: item.vendedorNombres, apPat: item.vendedorApPat, apMat: item.vendedorApMat }) : '',
    }
}

const toRequest = (item: SearchTareaDTO, status: TareaStatus): CreateTareaDTO => ({
    id: item.id,
    nombre: item.nombre || '',
    descripcion: item.descripcion || '',
    vencimiento: item.vencimiento,
    prioridad: item.prioridad?.name || 'media',
    tipo: item.tipo?.name || 'tarea',
    vendedor: item.vendedorId,
    status,
})

const fetchTasks = async (url: string): Promise<PagaebleResult<SearchTareaDTO>> => {
    const payload: WrapperResponse<PagaebleResult<SearchTareaDTO>> | null = await (await APIInvoke.invokeGET(url)).json()
    if (!payload?.ok || !payload.body) throw new Error(payload?.message || LOAD_ERROR)
    return payload.body
}

const plural = (count: number, singular: string, pluralText: string) => `${count} ${count === 1 ? singular : pluralText}`

export const useTaskDrawerModel = ({ open, onClose, onCountChange }: TaskDrawerProps) => {
    const history = useHistory()
    const onCountChangeRef = useRef(onCountChange)
    onCountChangeRef.current = onCountChange
    const { hasPermiso, hasRole } = useAuth()
    const [tab, setTab] = useState<TaskTab>('pendientes')
    const [openItems, setOpenItems] = useState<SearchTareaDTO[]>([])
    const [closedItems, setClosedItems] = useState<SearchTareaDTO[]>([])
    const [closedTotal, setClosedTotal] = useState(0)
    const [loaded, setLoaded] = useState(false)
    const [loading, setLoading] = useState(false)
    const [loadError, setLoadError] = useState<string | null>(null)
    const [actionError, setActionError] = useState<string | null>(null)
    const [toggling, setToggling] = useState<number[]>([])
    const [reloadToken, setReloadToken] = useState(0)
    const [formOpen, setFormOpen] = useState(false)
    const [detailId, setDetailId] = useState<number | null>(null)

    useEffect(() => {
        if (!open) {
            setFormOpen(false)
            setDetailId(null)
            setActionError(null)
            return undefined
        }
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setLoadError(null)
                const [openPage, closedPage] = await Promise.all([fetchTasks(OPEN_URL), fetchTasks(CLOSED_URL)])
                if (!active) return
                setOpenItems(openPage.items || [])
                setClosedItems(closedPage.items || [])
                setClosedTotal(closedPage.totalItems || 0)
                setLoaded(true)
            } catch (error) {
                if (active) setLoadError(errorMessage(error, LOAD_ERROR))
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [open, reloadToken])

    useEffect(() => {
        if (loaded) onCountChangeRef.current?.(openItems.length)
    }, [loaded, openItems.length])

    const reload = () => setReloadToken((token) => token + 1)

    const toggle = async (id: number) => {
        const item = openItems.find((task) => task.id === id) || closedItems.find((task) => task.id === id)
        if (!item || toggling.includes(id)) return
        try {
            setToggling((current) => [...current, id])
            setActionError(null)
            const next: TareaStatus = isClosed(item.status) ? 'abierta' : 'cerrada'
            const payload: WrapperResponse<unknown> = await (await APIInvoke.invokePUT(`/tareas/${id}`, toRequest(item, next))).json()
            if (!payload.ok) throw new Error(payload.message || SAVE_ERROR)
            const updated: SearchTareaDTO = { ...item, status: { name: next, displayName: next === 'cerrada' ? 'Cerrada' : 'Abierta' } }
            if (next === 'cerrada') {
                setOpenItems((current) => current.filter((task) => task.id !== id))
                setClosedItems((current) => [updated, ...current])
                setClosedTotal((total) => total + 1)
            } else {
                setClosedItems((current) => current.filter((task) => task.id !== id))
                setOpenItems((current) => [...current, updated])
                setClosedTotal((total) => Math.max(0, total - 1))
            }
            reload()
        } catch (error) {
            setActionError(errorMessage(error, SAVE_ERROR))
        } finally {
            setToggling((current) => current.filter((value) => value !== id))
        }
    }

    const items = [...openItems, ...closedItems].map((item) => toItem(item, toggling))
    const countOf = (group: TaskGroupKey) => items.filter((item) => item.group === group).length
    const counts: Record<TaskTab, number> = {
        pendientes: openItems.length,
        vencidas: countOf('vencidas'),
        hoy: countOf('hoy'),
        completadas: closedTotal,
    }

    const groups: TaskDrawerGroup[] = TAB_GROUPS[tab]
        .map((key) => ({ key, label: GROUP_LABELS[key], items: items.filter((item) => item.group === key) }))
        .filter((group) => group.items.length > 0)

    const subtitle = [
        plural(counts.pendientes, 'pendiente', 'pendientes'),
        counts.vencidas > 0 ? plural(counts.vencidas, 'vencida', 'vencidas') : null,
    ].filter(Boolean).join(' · ')

    return {
        tab,
        tabs: (Object.keys(TAB_LABELS) as TaskTab[]).map((key) => ({ key, label: TAB_LABELS[key], count: counts[key] })),
        groups,
        emptyMessage: EMPTY_MESSAGES[tab],
        subtitle: loaded ? subtitle : 'Cargando…',
        initialLoading: loading && !loaded,
        refreshing: loading && loaded,
        loadError,
        actionError,
        canWrite: hasRole(Roles.ROLE_ADMIN) || hasPermiso(Permisos.TAREAS_W),
        formOpen,
        detailId,
        selectTab: setTab,
        reload,
        toggle,
        dismissActionError: () => setActionError(null),
        openForm: () => setFormOpen(true),
        closeForm: () => setFormOpen(false),
        created: () => {
            setTab('pendientes')
            reload()
        },
        openDetail: (id: number) => setDetailId(id),
        closeDetail: () => setDetailId(null),
        editTarea: (id: number) => {
            onClose()
            history.push({ pathname: editTaskPath(id), state: { backUrl: history.location.pathname } satisfies TasksLocationState })
        },
    }
}
